import { useCallback, useEffect, useRef, useState } from "react";
import { zim } from "../../../../zegoConfig";
import axios from "../../../../api/axios";

import { MediaKind, ParsedMessage, ReceivedMessage, ReplyToData } from "../types";
import {
  buildParsedMessage,
  parseBackendChatMessage,
  saveMessagesToLocalStorage,
  resolveMediaKind,
  safeJsonParse,
  formatTimestamp,
  parseReplyData,
} from "../utils/chatHelpers";

interface UseChatMessagesParams {
  isLoggedIn: boolean;
  groupId: string | null;
  userId: string;
  userDokter: string;
  conversationID: string | undefined;
  decryptedToken: string;
  /**
   * Dipakai untuk memastikan user sudah join group Zego sebelum query history ZIM.
   * Opsional supaya hook ini tetap bisa dipakai tanpa useZegoChat.
   */
  ensureGroupReady?: () => Promise<boolean>;
  /** Menandakan join group sudah berhasil, dipakai untuk menarik ulang history ZIM. */
  isGroupReady?: boolean;
}

interface UseChatMessagesReturn {
  messages: ParsedMessage[];
  replyTo: ReplyToData | null;
  setReplyTo: (msg: ReplyToData | null) => void;
  loading: boolean;
  hasMore: boolean;
  failedAttachmentCount: number;
  attachmentSyncWarning: string | null;
  handleSend: (message: string) => Promise<void>;
  handleSendMedia: (
    file: File,
    mediaType: MediaKind,
  ) => Promise<void>;
  loadChatHistory: (next: any) => Promise<void>;
  refreshMessages: () => Promise<void>;
  retryFailedAttachments: () => Promise<void>;
}

interface FailedAttachmentSync {
  file: File;
  mediaType: MediaKind;
  messageID: string;
  replyTo: ReplyToData | null;
}

// conversationType 2 = Group di ZIM
const CONVERSATION_TYPE = 2;

// Jeda minimal antar refetch history backend yang dipicu event (anti-spam).
const REFETCH_THROTTLE_MS = 2500;

// Pasien bisa masuk room tepat ketika dokter baru menekan "mulai konsultasi",
// sehingga pesan sapaan dokter kadang tersimpan sepersekian detik setelah
// history pertama diambil. Beberapa refetch singkat menutup celah ini tanpa
// perlu refresh halaman manual.
const CATCH_UP_DELAYS_MS = [1500, 4000, 8000];

/**
 * Manages the full message lifecycle:
 * - Fetching history from ZIM (initial load + missed messages on re-enter)
 * - Listening for incoming messages
 * - Optimistic send for text messages
 * - Sending media via ZIM SDK
 */
export function useChatMessages({
  isLoggedIn,
  groupId,
  userId,
  userDokter,
  conversationID,
  decryptedToken,
  ensureGroupReady,
  isGroupReady = false,
}: UseChatMessagesParams): UseChatMessagesReturn {
  const [messages, setMessages] = useState<ParsedMessage[]>([]);
  const [messageIds, setMessageIds] = useState<Set<string>>(new Set());
  const [replyTo, setReplyTo] = useState<ReplyToData | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextMessage, setNextMessage] = useState<any>(null);
  const [failedAttachmentSyncs, setFailedAttachmentSyncs] = useState<
    Record<string, FailedAttachmentSync>
  >({});
  const [attachmentSyncWarning, setAttachmentSyncWarning] = useState<string | null>(null);

  // Track whether initial history load has been done for this session
  const initialLoadDoneRef = useRef(false);
  // Versi state-nya, supaya effect pelengkap ZIM ikut dievaluasi ulang
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  // Room yang initial load-nya sudah dimulai, supaya tidak dobel fetch
  const initialLoadStartedRef = useRef<string | null>(null);
  // Waktu sinkronisasi terakhir ke backend, dipakai untuk throttle refetch
  const lastBackendSyncRef = useRef(0);
  const localPreviewUrlsRef = useRef<Record<string, string>>({});

  // Keep refs in sync for use inside callbacks
  const messagesRef = useRef(messages);
  const messageIdsRef = useRef(messageIds);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  useEffect(() => {
    messageIdsRef.current = messageIds;
  }, [messageIds]);

  // ─── Helper: Pastikan user sudah join group sebelum kirim pesan ────────────────
  const ensureJoinedGroup = useCallback(async () => {
    if (!groupId) return;
    try {
      // Berdasarkan log: body:{group_id:{groupID:"26162"}} -> Error 100002 (input params error)
      // Ini menunjukkan SDK membungkus parameter kita.
      // Kita coba kirim String langsung untuk menghindari double-wrapping oleh SDK.
      // @ts-ignore
      await zim.joinGroup(String(groupId));
      } catch (err: any) {
        const errorCode = err.code || (err.message && JSON.parse(err.message as string)?.code);
        const errorMessage = err.message || "";

        if (errorCode === 108037 || errorMessage.includes("already belong to this group")) {
          // User already in group, this is a success case.
          return;
        }

        if (errorCode === 108044 || errorMessage.includes("group not exist")) {
          try {
            // Create group biasanya tetap membutuhkan objek
            await zim.createGroup({
              groupID: String(groupId),
              groupName: "Konsultasi Telemedicine",
            });
            // @ts-ignore
            await zim.joinGroup(String(groupId));
          } catch (createErr) {
            console.error("Critical: Gagal membuat group Zego:", createErr);
            throw createErr;
          }
        } else {
          console.error("Gagal join group Zego:", err);
          throw err;
        }
      }
  }, [groupId]);

  useEffect(() => {
    Object.values(localPreviewUrlsRef.current).forEach((url) => {
      URL.revokeObjectURL(url);
    });
    localPreviewUrlsRef.current = {};
    setHasMore(true);
    setNextMessage(null);
    setFailedAttachmentSyncs({});
    setAttachmentSyncWarning(null);
    initialLoadDoneRef.current = false;
    initialLoadStartedRef.current = null;
    lastBackendSyncRef.current = 0;
    setInitialLoadDone(false);
  }, [conversationID]);

  useEffect(() => {
    return () => {
      Object.values(localPreviewUrlsRef.current).forEach((url) => {
        URL.revokeObjectURL(url);
      });
      localPreviewUrlsRef.current = {};
    };
  }, []);

  // ─── Parse & push incoming messages ──────────────────────────────────────

  /**
   * Gabungkan pesan baru (dari backend maupun ZIM) ke state tanpa pernah
   * membuang pesan yang sudah tampil. Dipakai semua jalur pemuatan pesan
   * supaya perilaku dedupe & penggantian pesan optimistic selalu konsisten.
   */
  const mergeIncoming = useCallback((incoming: ParsedMessage[]) => {
    if (incoming.length === 0) return;

    setMessages((prev) => {
      const existingIds = new Set(prev.map((m) => m.messageID));
      const updated = [...prev];
      let changed = false;

      for (const newMsg of incoming) {
        if (existingIds.has(newMsg.messageID)) continue;

        // Pesan milik pasien sendiri: ganti bubble optimistic dengan versi final
        if (newMsg.isOwn || newMsg.isDoctor === false) {
          const optimisticIndex = updated.findIndex(
            (m) =>
              m.messageID.startsWith("local-") && m.message === newMsg.message,
          );
          if (optimisticIndex !== -1) {
            updated[optimisticIndex] = { ...newMsg, isOwn: true };
            existingIds.add(newMsg.messageID);
            changed = true;
            continue;
          }
        }

        updated.push(newMsg);
        existingIds.add(newMsg.messageID);
        changed = true;
      }

      if (!changed) return prev;

      updated.sort((a, b) => (a.rawTimestamp ?? 0) - (b.rawTimestamp ?? 0));
      saveMessagesToLocalStorage(updated);
      return updated;
    });

    setMessageIds((prev) => {
      const updated = new Set(prev);
      incoming.forEach((m) => updated.add(m.messageID));
      return updated;
    });
  }, []);

  const detectEndSession = useCallback(
    (rawMessage: unknown) => {
      if (typeof rawMessage !== "string" || !rawMessage.startsWith("end|")) return;
      const endId = rawMessage.split("|")[1];
      if (Number(endId) === Number(conversationID)) {
        window.dispatchEvent(new CustomEvent("consultation:ended"));
      }
    },
    [conversationID],
  );

  const parseAndPushMessages = useCallback(
    (
      messageList: ReceivedMessage[],
      dokterId: string,
      currentUserId: string,
    ) => {
      const newMessages: ParsedMessage[] = [];
      const seen = new Set<string>();

      for (const msg of messageList) {
        if (seen.has(msg.messageID)) continue;
        seen.add(msg.messageID);

        detectEndSession(msg.message);
        newMessages.push(buildParsedMessage(msg, dokterId, currentUserId));
      }

      mergeIncoming(newMessages);
    },
    [detectEndSession, mergeIncoming],
  );

  // ─── ZIM event listeners ──────────────────────────────────────────────────
  //
  // PENTING: Sengaja TIDAK memanggil zim.off() saat unmount.
  //
  // Ketika zim.off() dipanggil, ZIM SDK menginterpretasikannya sebagai sinyal
  // bahwa user selesai, lalu secara internal memanggil leaveGroup ke server
  // (terlihat di console sebagai [API.leaveGroup] dari index.js:1).
  //
  // Solusi: gunakan ref untuk handler agar selalu up-to-date, daftarkan
  // listener hanya SEKALI dan biarkan hidup selama sesi ZIM aktif.

  const parseAndPushMessagesRef = useRef(parseAndPushMessages);
  const userDokterRef = useRef(userDokter);
  const userIdRef = useRef(userId);

  useEffect(() => {
    parseAndPushMessagesRef.current = parseAndPushMessages;
  }, [parseAndPushMessages]);
  useEffect(() => {
    userDokterRef.current = userDokter;
    userIdRef.current = userId;
  }, [userDokter, userId]);

  // Diisi setelah fungsi refetch history backend terdefinisi di bawah.
  const conversationChangedRef = useRef<() => void>(() => {});

  const listenersOwnerRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoggedIn || !userDokter) return;

    // Listener ZIM disimpan satu callback per nama event (listenerMap.set),
    // jadi registrasi di sini sekaligus mengambil alih listener sisa milik
    // halaman ConnectingDoctor. Tanpa ini, handler lama masih memegang event
    // dan pesan yang masuk tidak pernah sampai ke halaman chat.
    const ownerKey = `${userDokter}:${groupId ?? ""}`;
    if (listenersOwnerRef.current === ownerKey) return;
    listenersOwnerRef.current = ownerKey;

    const handleMessage = (
      _zim: any,
      data: { messageList: ReceivedMessage[] },
    ) => {
      console.log("📩 Pesan masuk:", data?.messageList);
      if (!data?.messageList) return;
      // Gunakan ref terbaru — aman meski komponen re-render
      parseAndPushMessagesRef.current(
        data.messageList,
        userDokterRef.current,
        userIdRef.current,
      );
    };

    // Nama event sesuai SDK zego-zim-web: setiap pesan dikirim dua kali
    // (`<tipe>MessageReceived` dan `receive<Tipe>Message`). Event
    // "messageReceived" tidak pernah di-emit SDK, jadi tidak didaftarkan lagi.
    zim.on("groupMessageReceived", handleMessage);
    zim.on("receiveGroupMessage", handleMessage);
    zim.on("peerMessageReceived", handleMessage);
    zim.on("receivePeerMessage", handleMessage);

    // Ambil alih juga "conversationChanged" supaya handler lama dari halaman
    // ConnectingDoctor (yang memanggil navigate berulang) tidak ikut jalan,
    // sekaligus sebagai jaring pengaman: setiap perubahan percakapan memicu
    // sinkronisasi ulang history dari backend.
    zim.on("conversationChanged", () => {
      conversationChangedRef.current();
    });

    // ✅ Tidak ada return cleanup / zim.off() di sini
    // Mencegah ZIM SDK trigger internal leaveGroup saat komponen unmount
  }, [isLoggedIn, userDokter, groupId]);

  const updateSingleMessage = useCallback(
    (messageID: string, updater: (message: ParsedMessage) => ParsedMessage) => {
      setMessages((prev) => {
        const updated = prev.map((item) =>
          item.messageID === messageID ? updater(item) : item,
        );
        saveMessagesToLocalStorage(updated);
        return updated;
      });
    },
    [],
  );

  const clearLocalPreviewUrl = useCallback((messageID: string) => {
    const previewUrl = localPreviewUrlsRef.current[messageID];
    if (!previewUrl) return;

    URL.revokeObjectURL(previewUrl);
    delete localPreviewUrlsRef.current[messageID];
  }, []);

  const promoteBackendImageUrl = useCallback(
    (messageID: string, backendUrl: string) => {
      const probe = new Image();

      probe.onload = () => {
        updateSingleMessage(messageID, (message) => ({
          ...message,
          imageUrl: backendUrl,
        }));
        clearLocalPreviewUrl(messageID);
      };

      probe.onerror = () => {
        // Keep the local preview as a temporary fallback.
      };

      probe.src = backendUrl;
    },
    [clearLocalPreviewUrl, updateSingleMessage],
  );

  const syncAttachmentToLms = useCallback(
    async (
      file: File,
      mediaType: MediaKind,
      messageID: string,
      replySnapshot: ReplyToData | null,
    ) => {
      if (!groupId) {
        throw new Error("Room chat belum tersedia");
      }

      const mediaTypeMap: Record<MediaKind, number> = {
        image: 11,
        file: 12,
        audio: 13,
        video: 14,
      };

      const formData = new FormData();
      formData.append("roomId", groupId);
      formData.append("to_userId", userDokter);
      formData.append("from_userId", userId);
      formData.append("type_message", String(mediaTypeMap[mediaType]));
      formData.append("message_id", messageID);
      formData.append("file", file);

      if (replySnapshot) {
        formData.append("user_reply_id", replySnapshot.senderUserID);
        formData.append("message_id_reply", replySnapshot.messageID);
        formData.append("type_reply", replySnapshot.type_reply);
        formData.append("value_reply", replySnapshot.message);
      }

      const response = await axios.post("chat/attachment", formData, {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data?.data ?? response.data;
    },
    [decryptedToken, groupId, userDokter, userId],
  );

  const applySyncedAttachment = useCallback(
    (messageID: string, backendItem: any, fallbackReplyTo: ReplyToData | null) => {
      const parsed = parseBackendChatMessage(backendItem, userDokter);
      const localPreviewUrl = localPreviewUrlsRef.current[messageID];
      const shouldKeepLocalPreview =
        !!localPreviewUrl &&
        parsed?.mediaKind === "image" &&
        !!parsed?.imageUrl &&
        !parsed.imageUrl.startsWith("blob:");

      updateSingleMessage(messageID, (message) => ({
        ...(parsed || message),
        messageID,
        isOwn: true,
        replyTo: parsed?.replyTo || fallbackReplyTo || message.replyTo,
        message: parsed?.message || message.message,
        imageUrl:
          shouldKeepLocalPreview
            ? message.imageUrl
            : parsed?.imageUrl || message.imageUrl,
        fileName: parsed?.fileName || message.fileName,
        fileSize: parsed?.fileSize || message.fileSize,
        mediaKind: parsed?.mediaKind || message.mediaKind,
        syncStatus: "synced",
        isUploading: false,
        uploadFailed: false,
      }));

      if (shouldKeepLocalPreview && parsed?.imageUrl) {
        promoteBackendImageUrl(messageID, parsed.imageUrl);
        return;
      }

      if (parsed?.imageUrl || parsed?.mediaKind !== "image") {
        clearLocalPreviewUrl(messageID);
      }
    },
    [clearLocalPreviewUrl, promoteBackendImageUrl, updateSingleMessage, userDokter],
  );

  // ─── Load chat history (untuk pagination manual) ──────────────────────────

  const loadChatHistory = useCallback(
    async (next: any) => {
      // Guard: jangan load jika userDokter belum siap
      if (loading || !hasMore || !groupId || !userDokter) return;
      setLoading(true);

      try {
        const history = await zim.queryHistoryMessage(
          groupId,
          CONVERSATION_TYPE,
          {
            nextMessage: next,
            count: 100,
            reverse: false, // newest first, nextMessage = cursor ke pesan lebih lama
          },
        );

        console.log("📜 loadChatHistory (group):", history);

        const fetchedMessages = history.messageList as ReceivedMessage[];
        const newMessages: ParsedMessage[] = [];
        const newIds = new Set(messageIdsRef.current);

        for (const msg of fetchedMessages) {
          if (newIds.has(msg.messageID)) continue;
          newIds.add(msg.messageID);

          const isMedia = msg.type !== 1;
          const imageUrl =
            msg.largeImageDownloadUrl ||
            msg.thumbnailDownloadUrl ||
            msg.fileDownloadUrl;

          const parsedData = !isMedia ? safeJsonParse(msg.message) : null;
          const replyToData =
            parsedData && typeof parsedData === "object"
              ? parseReplyData(parsedData, userDokter)
              : null;

          newMessages.push({
            messageID: msg.messageID,
            message: isMedia ? "" : msg.message,
            timestamp: formatTimestamp(msg.timestamp),
            rawTimestamp: msg.timestamp,
            isDoctor: msg.senderUserID === userDokter,
            imageUrl: isMedia ? imageUrl : undefined,
            fileName: isMedia ? msg.fileName : undefined,
            fileSize: isMedia ? (msg as any).fileSize : undefined,
            type: isMedia ? "media" : "text",
            mediaKind: isMedia
              ? resolveMediaKind(msg.type, msg.fileName, imageUrl)
              : undefined,
            replyTo: replyToData,
            syncStatus: isMedia ? "synced" : undefined,
          });
        }

        if (newMessages.length > 0) {
          setMessages((prev) => {
            // Prepend older messages, sort untuk pastikan urutan
            const merged = [...newMessages, ...prev];
            merged.sort(
              (a, b) => (a.rawTimestamp ?? 0) - (b.rawTimestamp ?? 0),
            );
            saveMessagesToLocalStorage(merged);
            return merged;
          });
          setMessageIds(prev => {
            const updated = new Set(prev);
            newMessages.forEach(m => updated.add(m.messageID));
            return updated;
          });
        }

        setNextMessage(history.nextMessage);
        setHasMore(fetchedMessages.length > 0);
      } catch (err) {
        console.error("Failed to load chat history:", err);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupId, userDokter, loading, hasMore],
  );

  // ─── Helper: parse satu raw ZIM message ke ParsedMessage ────────────────

  const parseRawMessage = useCallback(
    (msg: ReceivedMessage): ParsedMessage => {
      const isMedia = msg.type !== 1;
      const imageUrl =
        msg.largeImageDownloadUrl ||
        msg.thumbnailDownloadUrl ||
        msg.fileDownloadUrl;
      const parsedData = !isMedia ? safeJsonParse(msg.message) : null;
      const replyToData =
        parsedData && typeof parsedData === "object"
          ? parseReplyData(parsedData, userDokter)
          : null;

      return {
        messageID: msg.messageID,
        message: isMedia ? "" : msg.message,
        timestamp: formatTimestamp(msg.timestamp),
        rawTimestamp: msg.timestamp,
        isDoctor: msg.senderUserID === userDokter,
        imageUrl: isMedia ? imageUrl : undefined,
        fileName: isMedia ? msg.fileName : undefined,
        fileSize: isMedia ? (msg as any).fileSize : undefined,
        type: isMedia ? "media" : "text",
        mediaKind: isMedia
          ? resolveMediaKind(msg.type, msg.fileName, imageUrl)
          : undefined,
        replyTo: replyToData,
        syncStatus: isMedia ? "synced" : undefined,
      };
    },
    [userDokter],
  );

  // ─── Load semua pesan saat pertama kali masuk / re-enter halaman chat ────
  //
  // PENTING tentang reverse:
  //   reverse: false (default) → ZIM mengembalikan pesan dari TERBARU ke TERLAMA
  //                               nextMessage menunjuk ke pesan lebih lama lagi
  //   reverse: true            → ZIM mengembalikan pesan dari TERLAMA ke TERBARU
  //                               jika ada 500 pesan & count=100, hanya dapat 100 pesan pertama
  //
  // Strategi yang benar:
  //   1. Fetch tanpa reverse (newest first), count=100 per batch
  //   2. Loop terus pakai nextMessage sampai tidak ada lagi
  //   3. Setelah semua terkumpul, sort ascending untuk tampilan chat

  // ─── Fetch history dari backend (sumber kebenaran utama) ─────────────────
  //
  // ZIM queryHistoryMessage hanya mengembalikan pesan yang dikirim SETELAH
  // user bergabung ke group. Pesan yang dikirim dokter sebelum pasien join
  // tidak akan muncul dari ZIM. Backend menyimpan semua pesan sejak awal,
  // sehingga backend menjadi sumber utama history yang lengkap.

  const fetchBackendHistory = useCallback(async (): Promise<
    ParsedMessage[]
  > => {
    console.log(
      "🔄 Fetching backend history for room ID:",
      groupId,
    );
    if (!groupId || !decryptedToken) return [];

    try {
      const response = await axios.get(`chat/${groupId}`, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });

      const rawList: any[] = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data?.data?.List)
            ? response.data.data.List
          : Array.isArray(response.data?.messages)
            ? response.data.messages
            : [];

      console.log(`📡 Backend history: ${rawList.length} pesan`);

      const parsed: ParsedMessage[] = [];

      for (const item of rawList) {
        const source = item?.List || item;
        const parsedMessage = parseBackendChatMessage(source, userDokter);
        if (!parsedMessage) continue;

        // Deteksi end session dari backend history juga
        detectEndSession(parsedMessage.message);

        parsed.push(parsedMessage);
      }

      return parsed;
    } catch (err) {
      console.warn("⚠️ Gagal fetch backend history, fallback ke ZIM:", err);
      return [];
    }
  }, [decryptedToken, detectEndSession, groupId, userDokter]);

  /**
   * Tarik history dari backend lalu langsung tampilkan.
   * Sengaja TIDAK bergantung pada status login ZIM: pesan dokter (termasuk
   * sapaan otomatis saat konsultasi dimulai) sudah tersimpan di LMS, jadi
   * chat harus bisa tampil walaupun sesi ZIM belum siap.
   */
  const syncBackendHistory = useCallback(async (): Promise<number> => {
    if (!groupId || !userDokter || !decryptedToken) return 0;

    lastBackendSyncRef.current = Date.now();
    const backendMessages = await fetchBackendHistory();
    mergeIncoming(backendMessages);
    return backendMessages.length;
  }, [decryptedToken, fetchBackendHistory, groupId, mergeIncoming, userDokter]);

  /**
   * Tarik history dari ZIM sebagai pelengkap (media & pesan realtime).
   * Best-effort: kegagalan di sini tidak boleh menghapus pesan dari backend.
   */
  const syncZimHistory = useCallback(async () => {
    if (!groupId || !userDokter || !isLoggedIn) return;

    // Group Zego baru dibuat backend saat dokter memulai konsultasi, sehingga
    // query history bisa gagal bila join group belum tuntas.
    if (ensureGroupReady) {
      try {
        const joined = await ensureGroupReady();
        if (!joined) {
          console.warn("⚠️ Join group belum berhasil, lewati history ZIM");
          return;
        }
      } catch (err) {
        console.warn("⚠️ ensureGroupReady error:", err);
        return;
      }
    }

    const zimMessages: ParsedMessage[] = [];
    let cursor: any = undefined;
    let batchCount = 0;
    const MAX_BATCHES = 20;

    try {
      while (batchCount < MAX_BATCHES) {
        const history = await zim.queryHistoryMessage(
          groupId,
          CONVERSATION_TYPE,
          {
            nextMessage: cursor,
            count: 100,
            reverse: false, // newest first
          },
        );

        const batch = history.messageList as ReceivedMessage[];
        console.log(`📜 ZIM Batch ${batchCount + 1}: ${batch.length} pesan`);

        if (batch.length === 0) break;

        for (const msg of batch) {
          detectEndSession(msg.message);
          zimMessages.push(parseRawMessage(msg));
        }

        batchCount++;
        if (batch.length < 100 || !history.nextMessage) break;
        cursor = history.nextMessage;
      }
    } catch (err) {
      // Penting: pesan dari backend yang sudah tampil tetap dipertahankan.
      console.warn("⚠️ Gagal ambil history ZIM (pakai data backend):", err);
    }

    mergeIncoming(zimMessages);
  }, [
    detectEndSession,
    ensureGroupReady,
    groupId,
    isLoggedIn,
    mergeIncoming,
    parseRawMessage,
    userDokter,
  ]);

  const loadAllMessages = useCallback(async () => {
    if (!groupId || !userDokter) return;

    console.log("🔄 Loading all messages on enter chat page...");
    setLoading(true);

    try {
      // ── 1. Backend dulu: tampilkan secepatnya, tanpa menunggu ZIM ──────────
      await syncBackendHistory();
      initialLoadDoneRef.current = true;
      setInitialLoadDone(true);
      setHasMore(false);

      // ── 2. ZIM menyusul sebagai pelengkap ─────────────────────────────────
      await syncZimHistory();
    } catch (err) {
      console.error("❌ Failed to load all messages:", err);
    } finally {
      setLoading(false);
    }
  }, [groupId, userDokter, syncBackendHistory, syncZimHistory]);

  // Simpan fungsi refetch untuk dipakai listener ZIM (throttled).
  useEffect(() => {
    conversationChangedRef.current = () => {
      const now = Date.now();
      if (now - lastBackendSyncRef.current < REFETCH_THROTTLE_MS) return;
      syncBackendHistory();
    };
  }, [syncBackendHistory]);

  // ─── Initial load: cukup butuh room & token, tidak menunggu ZIM ───────────
  useEffect(() => {
    if (!groupId || !userDokter || !decryptedToken) return;
    if (initialLoadStartedRef.current === groupId) return;
    initialLoadStartedRef.current = groupId;

    loadAllMessages();
  }, [groupId, userDokter, decryptedToken, loadAllMessages]);

  // ZIM baru siap setelah initial load (atau join group baru berhasil)
  // → lengkapi pesan dari ZIM tanpa mengganggu pesan yang sudah tampil.
  useEffect(() => {
    if (!isLoggedIn || !groupId || !userDokter || !initialLoadDone) return;
    syncZimHistory();
  }, [
    isLoggedIn,
    groupId,
    userDokter,
    initialLoadDone,
    isGroupReady,
    syncZimHistory,
  ]);

  // ─── Catch-up: pesan pembuka dokter bisa tersimpan sepersekian detik ──────
  // setelah pasien masuk room. Beberapa refetch singkat + refetch saat halaman
  // kembali aktif membuat chat muncul tanpa perlu refresh manual.
  useEffect(() => {
    if (!groupId || !userDokter || !decryptedToken) return;

    const timers = CATCH_UP_DELAYS_MS.map((delay) =>
      setTimeout(() => {
        syncBackendHistory();
      }, delay),
    );

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        syncBackendHistory();
      }
    };

    window.addEventListener("focus", handleVisibility);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("focus", handleVisibility);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [groupId, userDokter, decryptedToken, syncBackendHistory]);

  // ─── Jaring pengaman saat sesi ZIM belum siap ────────────────────────────
  // Selama ZIM belum login, pesan realtime tidak bisa diterima. Polling ringan
  // ke backend menjaga chat tetap hidup, dan otomatis berhenti begitu ZIM siap.
  useEffect(() => {
    if (!groupId || !userDokter || !decryptedToken) return;
    if (isLoggedIn) return;

    const interval = setInterval(() => {
      syncBackendHistory();
    }, 5000);

    return () => clearInterval(interval);
  }, [groupId, userDokter, decryptedToken, isLoggedIn, syncBackendHistory]);

  const refreshMessages = useCallback(async () => {
    initialLoadDoneRef.current = false; // reset flag
    setInitialLoadDone(false);
    setMessages([]);
    setMessageIds(new Set());
    await loadAllMessages();
  }, [loadAllMessages]);


  // Logic for initial loading is now handled by the main useEffect above


  // ─── Send text ────────────────────────────────────────────────────────────

  const handleSend = async (message: string) => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return;

    const payloadData: any = {
      message: trimmedMessage,
      type: "text",
      time: Date.now(),
    };

    if (replyTo) {
      let replyMessageContent = replyTo.message;
      if (typeof replyTo.message === "string") {
        const parsed = safeJsonParse(replyTo.message);
        if (parsed) replyMessageContent = parsed.message || replyTo.message;
      }
      payloadData.user_reply_id =
        replyTo.senderUserID || (replyTo.isDoctor ? userDokter : userId);
      payloadData.message_id_reply = replyTo.messageID;
      payloadData.type_reply = replyTo.type_reply || "text";
      payloadData.value_reply = replyMessageContent;
    }

    const messageString = JSON.stringify(payloadData);

    // Optimistic update
    const optimisticId = `local-${Date.now()}`;
    const now = Date.now();
    const optimisticMsg: ParsedMessage = {
      messageID: optimisticId,
      message: messageString,
      type: "text",
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
      rawTimestamp: now,
      isDoctor: false,
      replyTo: replyTo,
    };

    setMessages((prev) => {
      const updated = [...prev, optimisticMsg];
      saveMessagesToLocalStorage(updated);
      return updated;
    });
    setMessageIds((prev) => new Set([...prev, optimisticId]));
    setReplyTo(null);

    try {
      await axios.post(
        "chat",
        {
          to_userId: userDokter,
          from_userId: userId,
          message: messageString,
          type_message: 1,
          roomId: groupId,
        },
        { headers: { Authorization: `Bearer ${decryptedToken}` } },
      );
    } catch (error: any) {
      console.error(
        "Failed to send message:",
        error?.response?.data || error.message,
      );
      // Rollback optimistic message
      setMessages((prev) => prev.filter((m) => m.messageID !== optimisticId));
      setMessageIds((prev) => {
        const next = new Set(prev);
        next.delete(optimisticId);
        return next;
      });
    }
  };

  // ─── Send media ───────────────────────────────────────────────────────────

  const handleSendMedia = async (
    file: File,
    mediaType: MediaKind,
  ) => {
    if (!groupId) return;

    try {
      await ensureJoinedGroup();
    } catch (e) {
      alert("Koneksi chat terputus. Silakan coba lagi.");
      return;
    }

    const mediaMessageMap: Record<string, any> = {
      image: { fileLocalPath: file, type: 11 },
      file: { fileLocalPath: file, type: 12 },
      audio: { fileLocalPath: file, type: 13, audioDuration: 100 },
      video: { fileLocalPath: file, type: 14, videoDuration: 100 },
    };

    const mediaMessageObj = mediaMessageMap[mediaType];
    if (!mediaMessageObj) return;

    // ── Optimistic: tampilkan preview media + indikator loading ──────────────
    const optimisticId = `local-media-${Date.now()}`;
    const now = Date.now();
    const previewUrl =
      mediaType === "image" || mediaType === "video"
        ? URL.createObjectURL(file)
        : undefined;

    const optimisticMsg: ParsedMessage = {
      messageID: optimisticId,
      message: "[media]",
      type: "media",
      timestamp: new Date(now).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
      rawTimestamp: now,
      isDoctor: false,
      imageUrl: previewUrl,
      fileName: file.name,
      fileSize: file.size,
      replyTo: null,
      isUploading: true,
    };

    setMessages((prev) => {
      const updated = [...prev, optimisticMsg];
      return updated;
    });
    setMessageIds((prev) => new Set([...prev, optimisticId]));

    try {
      const replySnapshot = replyTo;
      const res = await zim.sendMessage(
        mediaMessageObj,
        groupId,
        CONVERSATION_TYPE,
        { priority: 1 },
      );

      const msg = res.message;
      if (previewUrl) {
        localPreviewUrlsRef.current[msg.messageID] = previewUrl;
      }
      const newMsg: ParsedMessage = {
        messageID: msg.messageID,
        message: "[media]",
        type: "media",
        timestamp: formatTimestamp(msg.timestamp),
        rawTimestamp: msg.timestamp,
        isDoctor: false,
        imageUrl:
          msg.largeImageDownloadUrl ||
          msg.fileDownloadUrl ||
          msg.thumbnailDownloadUrl ||
          previewUrl,
        fileName: msg.fileName || file.name,
        fileSize: (msg as any).fileSize,
        isUploading: false,
        replyTo: replySnapshot,
        mediaKind: mediaType,
        syncStatus: "pending",
      };

      setMessages((prev) => {
        const updated = prev.map((m) =>
          m.messageID === optimisticId ? newMsg : m,
        );
        saveMessagesToLocalStorage(updated);
        return updated;
      });
      setMessageIds((prev) => {
        const next = new Set(prev);
        next.delete(optimisticId);
        next.add(msg.messageID);
        return next;
      });
      setReplyTo(null);

      try {
        const syncedMessage = await syncAttachmentToLms(
          file,
          mediaType,
          msg.messageID,
          replySnapshot,
        );

        applySyncedAttachment(msg.messageID, syncedMessage, replySnapshot);
        setFailedAttachmentSyncs((prev) => {
          const updated = { ...prev };
          delete updated[msg.messageID];
          return updated;
        });
        setAttachmentSyncWarning(null);
      } catch (syncError: any) {
        console.error(
          "Failed to sync media attachment to LMS:",
          syncError?.response?.data || syncError?.message || syncError,
        );
        setFailedAttachmentSyncs((prev) => ({
          ...prev,
          [msg.messageID]: {
            file,
            mediaType,
            messageID: msg.messageID,
            replyTo: replySnapshot,
          },
        }));
        updateSingleMessage(msg.messageID, (message) => ({
          ...message,
          syncStatus: "failed",
        }));
        setAttachmentSyncWarning(
          "Ada lampiran yang sudah terkirim ke chat, tetapi belum tersimpan permanen di LMS.",
        );
      }
    } catch (error) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      console.error("Failed to send media message:", error);
      setMessages((prev) =>
        prev.map((m) =>
          m.messageID === optimisticId
            ? { ...m, isUploading: false, uploadFailed: true }
            : m,
        ),
      );
      // Alert the user that the send failed
      alert("Gagal mengirim media. Silakan coba lagi.");
    }
  };

  const retryFailedAttachments = useCallback(async () => {
    const pendingRetries = Object.values(failedAttachmentSyncs);
    if (pendingRetries.length === 0) return;

    let hasFailure = false;

    for (const item of pendingRetries) {
      updateSingleMessage(item.messageID, (message) => ({
        ...message,
        syncStatus: "pending",
      }));

      try {
        const syncedMessage = await syncAttachmentToLms(
          item.file,
          item.mediaType,
          item.messageID,
          item.replyTo,
        );
        applySyncedAttachment(item.messageID, syncedMessage, item.replyTo);
        setFailedAttachmentSyncs((prev) => {
          const updated = { ...prev };
          delete updated[item.messageID];
          return updated;
        });
      } catch (error: any) {
        hasFailure = true;
        updateSingleMessage(item.messageID, (message) => ({
          ...message,
          syncStatus: "failed",
        }));
        console.error(
          "Retry attachment sync failed:",
          error?.response?.data || error?.message || error,
        );
      }
    }

    setAttachmentSyncWarning(
      hasFailure
        ? "Sebagian lampiran masih belum tersimpan di LMS. Coba sync ulang lagi."
        : null,
    );
  }, [
    applySyncedAttachment,
    failedAttachmentSyncs,
    syncAttachmentToLms,
    updateSingleMessage,
  ]);

  return {
    messages,
    replyTo,
    setReplyTo,
    loading,
    hasMore,
    failedAttachmentCount: Object.keys(failedAttachmentSyncs).length,
    attachmentSyncWarning,
    handleSend,
    handleSendMedia,
    loadChatHistory,
    refreshMessages,
    retryFailedAttachments,
  };
}
